import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Form from 'react-bootstrap/Form';
import Calendar from 'react-calendar';
import Stack from 'react-bootstrap/Stack';
import { businessTodayDate } from '../../../utils/businessTime';
import { CALENDAR_PROPS } from '../FilterToolbar/FilterToolbar';
import '../FilterToolbar/FilterToolbar.css';
import './SingleDatePicker.css';

function formatDate(date) {
  if (!date) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const isPastDate = (date) => {
  // Before today on the business calendar, not the browser's.
  const today = businessTodayDate();

  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  return d < today;
};

export default function SingleDatePicker({
  value,
  disabled = false,
  holidays = [],
  placeholder = 'Select Date',
  useClickOutside,
  onSelect,
  nonWorkingDays = [],
  readOnly = false,
  // Draw the calendar on the page body, pinned to the field, so a pop-up
  // around it cannot cut it off or cover it.
  menuPortal = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const calendarRef = useRef(null);
  const fieldRef = useRef(null);
  const [popPos, setPopPos] = useState(null);

  useClickOutside(calendarRef, () => setIsOpen(false));

  useEffect(() => {
    if (value) {
      const [d, m, y] = value.split('/');
      // Local midnight — new Date('YYYY-MM-DD') is UTC and slips a day west of UTC.
      setSelectedDate(new Date(Number(y), Number(m) - 1, Number(d)));
    }
  }, [value]);

  // Keeps a portalled calendar under the field (or above it, when the window
  // has no room below) while the page or pop-up scrolls.
  useLayoutEffect(() => {
    if (!menuPortal || !isOpen) return undefined;
    const place = () => {
      const r = fieldRef.current?.getBoundingClientRect();
      if (!r) return;
      const h = calendarRef.current?.offsetHeight || 340;
      const below = window.innerHeight - r.bottom;
      const top = below < h + 8 && r.top > h + 8 ? r.top - h - 4 : r.bottom + 4;
      const w = calendarRef.current?.offsetWidth || 300;
      setPopPos({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [menuPortal, isOpen]);

  const holidaySet = new Set(holidays);

  const getWeekday = (date) => date.toLocaleDateString('en-GB', { weekday: 'long' });

  function getLocalDateString(date) {
    return date.toLocaleDateString('en-CA'); // Outputs YYYY-MM-DD
  }

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const formatted = formatDate(date);
    onSelect(formatted);
    setIsOpen(false);
  };

  const popup = (
    <div
      ref={calendarRef}
      className="calendar-popup ftb-datepick-pop"
      style={menuPortal ? { position: 'fixed', top: popPos?.top ?? -9999, left: popPos?.left ?? 0, zIndex: 10060 } : undefined}
    >
      <Calendar
        onChange={handleDateChange}
        value={selectedDate}
        {...CALENDAR_PROPS}
        tileDisabled={({ date, view }) => {
          if (view !== 'month') return false;

          const dateKey = getLocalDateString(date);
          const weekday = getWeekday(date);

          return isPastDate(date) || holidaySet.has(dateKey) || nonWorkingDays.includes(weekday);
        }}
      />
    </div>
  );

  return (
    <div className="position-relative" ref={fieldRef}>
      <Stack direction="horizontal">
        <Form.Control
          className="single-date-input"
          type="text"
          placeholder={placeholder}
          value={selectedDate ? formatDate(selectedDate) : ''}
          readOnly={readOnly}
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(true)}
        />
      </Stack>

      {isOpen && (menuPortal ? createPortal(popup, document.body) : popup)}
    </div>
  );
}
